import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { getProductById } from '@/api/hotelService';
import { Product } from '@/types';
import { Loader2, Minus, Plus, ShoppingBag } from 'lucide-react';
import { QuickCheckoutSheet } from '@/components/checkout/QuickCheckoutSheet';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function ProductDetailPage() {
  const { hotelId, productId } = useParams<{ hotelId: string; productId: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { t } = useLanguage();
  const query = useQuery();
  const { addToCart, items, updateQuantity, totalItems, totalPrice } = useCart();
  const navigate = useNavigate();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  
  // Check if product is in cart
  const cartItem = items.find(item => item?.product?.id === productId);
  const cartQuantity = cartItem ? cartItem.quantity : 0;

  useEffect(() => {
    const fetchProduct = async () => {
      if (!hotelId || !productId) {
        navigate(`/${hotelId}`);
        return;
      }
      
      try {
        setLoading(true);
        const token = query.get('token');
        const productData = await getProductById(hotelId, productId, token);
        
        if (!productData) {
          navigate(`/${hotelId}`);
          return;
        }
        
        setProduct(productData);
        
        // Set initial quantity if product is in cart
        if (cartQuantity > 0) {
          setQuantity(cartQuantity);
        }
        
      } catch (error) {
        console.error('Error fetching product:', error);
        navigate(`/${hotelId}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [hotelId, productId, navigate, cartQuantity]);

  const handleAddToCart = () => {
    if (product) {
      if (cartQuantity > 0) {
        updateQuantity(product.id, quantity);
      } else {
        addToCart(product, quantity);
      }
      navigate(`/${hotelId}`);
    }
  };
  
  const handleCheckout = () => {
    if (product && cartQuantity === 0) {
      addToCart(product, quantity);
    } else if (product) {
      updateQuantity(product.id, quantity);
    }
    setCheckoutOpen(true)
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header showBackButton />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-primary" />
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header showBackButton />
      
      <main className="flex-1 overflow-y-auto pb-24 hide-scrollbar">
        <div className="w-full aspect-square relative">
          <img
            src={product.image}
            alt={product.name}
            className="h-full w-full object-cover"
          />
          
          {cartQuantity > 0 && (
            <div className="absolute top-4 right-4 bg-primary text-primary-foreground text-sm font-medium h-8 w-8 flex items-center justify-center rounded-full">
              <ShoppingBag className="h-4 w-4" />
            </div>
          )}
        </div>
        
        <div className="p-4">
          <div className="mb-4">
            <div className="flex justify-between items-start">
              <h1 className="text-xl font-bold">{product.name}</h1>
              <p className="text-xl font-bold text-primary">${product.price.toFixed(2)}</p>
            </div>
            
            <p className="text-sm text-muted-foreground mt-2">
              {product.description}
            </p>
          </div>
          
          <div className="mt-6 flex items-center justify-between">
            <div className="flex border rounded-md">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                className="h-9 px-3"
              >
                <Minus className="h-3 w-3" />
              </Button>
              <div className="flex items-center justify-center w-10 h-9 border-x">
                {quantity}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQuantity(quantity + 1)}
                className="h-9 px-3"
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            
            <p className="font-bold">
              Total: ${(product.price * quantity).toFixed(2)}
            </p>
          </div>
        </div>
      </main>
      
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t safe-bottom">
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline"
            className="h-12 text-base"
            onClick={handleAddToCart}
          >
            {cartQuantity > 0 ? t('updateCart') : t('addToCart')}
          </Button>
          
          <Button 
            className="h-12 text-base"
            onClick={handleCheckout}
          >
            {t('proceedToCheckout')}
          </Button>
        </div>
        
        {totalItems > 0 && totalItems !== (cartQuantity > 0 ? 1 : 0) && (
          <div className="flex justify-between items-center mt-3 text-sm text-muted-foreground">
            <span>{totalItems} {totalItems === 1 ? 'item' : 'items'} in cart</span>
            <span>${totalPrice.toFixed(2)}</span>
          </div>
        )}
      </div>


      <QuickCheckoutSheet 
        open={checkoutOpen} 
        onOpenChange={setCheckoutOpen} 
      />
    </div>
  );
}